import { useState, useEffect } from "react";
import { useLocation, useSearch } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { 
  Search, 
  Brain,
  Briefcase,
  User,
  LogOut,
  ChevronUp,
  ChevronRight,
  FileText,
  Sliders,
  Heart,
  Settings,
  Bot,
} from "lucide-react";
import { Link } from "wouter";
import { apiRequest, clearUserCache } from "@/lib/queryClient";
import { TalentureLogo } from "./TalentureLogo";

const DashboardIcon = ({ className = "" }: { className?: string }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={className}>
    <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

interface StandardSidebarProps {
  activeRoute?: string;
}

export default function StandardSidebar({ activeRoute }: StandardSidebarProps) {
  const { user } = useAuth();
  const [location] = useLocation();
  const searchString = useSearch();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  
  const currentRoute = activeRoute || location;
  const currentSection = new URLSearchParams(searchString).get("section") || "overview";
  
  const displayName = user?.firstName && user?.lastName 
    ? `${user.firstName} ${user.lastName}`
    : user?.email || "User";

  const initials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`
    : user?.email?.[0]?.toUpperCase() || "U";

  const isProfileActive = currentRoute.startsWith("/candidate-profile");

  // Controlled collapsible state that syncs with active route
  const [profileOpen, setProfileOpen] = useState(isProfileActive);

  // Sync collapsible state with route changes (auto-expand when navigating to route)
  useEffect(() => {
    if (isProfileActive) setProfileOpen(true);
  }, [isProfileActive]);

  const profileSections = [
    { id: "overview", label: "Overview", icon: User },
    { id: "resume", label: "Resume", icon: FileText },
    { id: "preferences", label: "Preferences", icon: Sliders },
    { id: "values", label: "Values", icon: Heart },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  const simpleNavigationItems: Array<{
    path: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    isActive: boolean;
    badge?: string;
  }> = [
    {
      path: "/candidate-dashboard",
      label: "Dashboard",
      icon: DashboardIcon,
      isActive: currentRoute === "/candidate-dashboard" || currentRoute === "/" || currentRoute === "/dashboard"
    },
    {
      path: "/job-search",
      label: "Job Search",
      icon: Search,
      isActive: currentRoute === "/job-search"
    },
    {
      path: "/job-recommendations",
      label: "AI Job Recommendations",
      icon: Brain,
      isActive: currentRoute === "/job-recommendations"
    },
    {
      path: "/my-applications",
      label: "Applications",
      icon: Briefcase,
      isActive: currentRoute === "/my-applications"
    },
    {
      path: "/agents",
      label: "AI Agents",
      icon: Bot,
      isActive: currentRoute === "/agents" || currentRoute.startsWith("/agents/")
    },
  ];

  const handleLogout = async () => {
    try {
      await clearUserCache();
      
      const response = await apiRequest('/api/auth/logout', {
        method: 'POST'
      });
      if (response.ok) {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Logout failed:', error);
      await clearUserCache();
      window.location.href = '/api/auth/logout';
    }
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-base-gray-200">
      <SidebarHeader className="p-4 border-b border-base-gray-200">
        <TalentureLogo />
      </SidebarHeader>
      
      <SidebarContent className="px-3 py-4 group-data-[collapsible=icon]:px-0">
        <SidebarMenu>
          {simpleNavigationItems.map((item) => {
            const IconComponent = item.icon;
            
            return (
              <SidebarMenuItem key={item.path}>
                <SidebarMenuButton 
                  asChild 
                  size="lg"
                  isActive={item.isActive}
                  tooltip={item.label}
                  className="h-11 px-3 rounded-lg font-medium"
                >
                  <Link to={item.path}>
                    <IconComponent className="w-5 h-5" />
                    <span>{item.label}</span>
                    {item.badge && (
                      <Badge className="ml-auto bg-red-500 text-white text-xs">
                        {item.badge}
                      </Badge>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}

          {/* Profile - Collapsible (expanded) / Dropdown (collapsed) */}
          {isCollapsed ? (
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    isActive={isProfileActive}
                    tooltip="Profile"
                    className="h-11 px-3 rounded-lg font-medium"
                  >
                    <User className="w-5 h-5" />
                    <span>Profile</span>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="start" sideOffset={4}>
                  <DropdownMenuLabel>Profile</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {profileSections.map((section) => {
                    const IconComponent = section.icon;
                    const sectionUrl = section.id === "overview" 
                      ? "/candidate-profile" 
                      : `/candidate-profile?section=${section.id}`;
                    return (
                      <DropdownMenuItem key={section.id} asChild>
                        <Link to={sectionUrl} className="cursor-pointer">
                          <IconComponent className="mr-2 h-4 w-4" />
                          <span>{section.label}</span>
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          ) : (
            <Collapsible 
              asChild 
              open={profileOpen}
              onOpenChange={setProfileOpen}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    isActive={isProfileActive}
                    tooltip="Profile"
                    className="h-11 px-3 rounded-lg font-medium"
                  >
                    <User className="w-5 h-5" />
                    <span>Profile</span>
                    <ChevronRight className="ml-auto w-4 h-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {profileSections.map((section) => {
                      const IconComponent = section.icon;
                      const sectionUrl = section.id === "overview" 
                        ? "/candidate-profile" 
                        : `/candidate-profile?section=${section.id}`;
                      const isActive = isProfileActive && currentSection === section.id;
                      
                      return (
                        <SidebarMenuSubItem key={section.id}>
                          <SidebarMenuSubButton 
                            asChild 
                            isActive={isActive}
                            className="hover:bg-transparent active:bg-transparent data-[active=true]:bg-transparent hover:text-base-primary"
                          >
                            <Link to={sectionUrl}>
                              <IconComponent className="w-4 h-4" />
                              <span>{section.label}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      );
                    })}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          )}
        </SidebarMenu>
      </SidebarContent>
      
      <SidebarFooter className="p-2 border-t border-base-gray-200 group-data-[collapsible=icon]:px-0">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="w-full data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="w-8 h-8 group-data-[collapsible=icon]:hidden">
                    <AvatarImage src={user?.profileImageUrl || undefined} alt={displayName} />
                    <AvatarFallback className="bg-base-primary text-white text-sm">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left text-sm leading-tight min-w-0 group-data-[collapsible=icon]:hidden">
                    <span className="truncate font-medium block">{displayName}</span>
                    <span className="truncate text-xs text-muted-foreground block">
                      {user?.role === 'candidate' ? 'Free Account' : 'Pro Account'}
                    </span>
                  </div>
                  <ChevronUp className="w-4 h-4 ml-auto group-data-[collapsible=icon]:ml-0" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                className="w-56" 
                side="top"
                align="end"
                sideOffset={8}
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{displayName}</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/candidate-profile" className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                  onClick={handleLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
